/* eslint-disable @next/next/no-img-element */
import { COLORS } from '@styles/colors'
import { Container, Section } from '@styles/global.styled'
import { compareAsc, format, getMonth, parseISO } from 'date-fns'
import React, { useEffect } from 'react'
import { useGenerateImage } from 'recharts-to-png'
import { useInView } from 'react-intersection-observer'
import {
  PRICE_TRACKER_CHART_ID,
  registerPriceTrackerChartCapture,
  unregisterPriceTrackerChartCapture,
} from '../priceTrackerChartCapture'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import PoweredBy from '../PoweredBy'
import StatisticItems from '../StatisticItems'
import SuburbProfileFilters from '../SuburbProfileFilters'
import useMedianPrices from '@hooks/useMedianPrices'
import * as S from './MedianPrice.styled'

const buildChartData = (houseTrend, unitTrend) => {
  const map = {}
  if (houseTrend?.find((it) => it.price > 0)) {
    houseTrend?.forEach((item) => {
      map[item.date] ??= { date: item.date }
      map[item.date].house = item.price
    })
  }

  if (unitTrend?.find((it) => it.price > 0)) {
    unitTrend?.forEach((item) => {
      map[item.date] ??= { date: item.date }
      map[item.date].unit = item.price
    })
  }

  return Object.values(map).sort((a, b) =>
    compareAsc(parseISO(a.date), parseISO(b.date))
  )
}
const formatPrice = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}m`
  return `$${(value / 1000).toFixed(0)}k`
}

const getYearsInChartData = (chartData) => {
  const years = new Set()
  chartData.forEach((row) => {
    years.add(parseISO(row.date).getFullYear())
  })
  return [...years].sort((a, b) => a - b)
}

const buildJanuaryDate = (year, chartData) => {
  const janRow = chartData.find((row) => getMonth(parseISO(row.date)) === 0)
  if (janRow) {
    return janRow.date.replace(/^\d{4}/, String(year))
  }

  const sample = chartData[0]?.date
  const parts = sample?.match(/^(\d{4})-(\d{2})(.*)$/)
  if (parts) {
    return `${year}-01${parts[3]}`
  }

  return `${year}-01-01`
}

const buildYearStartTicks = (chartData) =>
  getYearsInChartData(chartData).map((year) =>
    buildJanuaryDate(year, chartData)
  )

const extendChartDataForAxis = (
  chartData,
  extraDates = [],
  paddingYears = null
) => {
  if (!chartData.length) return chartData

  const years = paddingYears ?? getYearsInChartData(chartData)
  const existingDates = new Set(chartData.map((row) => row.date))
  const paddingRows = years
    .map((year) => buildJanuaryDate(year, chartData))
    .filter((date) => !existingDates.has(date))
    .map((date) => ({ date }))

  const datesAfterPadding = new Set([
    ...existingDates,
    ...paddingRows.map((row) => row.date),
  ])
  const extraPaddingRows = extraDates
    .filter((date) => date && !datesAfterPadding.has(date))
    .map((date) => ({ date }))

  if (!paddingRows.length && !extraPaddingRows.length) return chartData

  return [...chartData, ...paddingRows, ...extraPaddingRows].sort((a, b) =>
    compareAsc(parseISO(a.date), parseISO(b.date))
  )
}

const MedianPrice = ({
  medianPrices: initialMedianPrices,
  suburb,
  slug,
  availableBedrooms,
}) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })
  const [bedrooms, setBedrooms] = React.useState('Overall')
  const [type, setType] = React.useState('all')
  const { medianPrices } = useMedianPrices(
    slug,
    type,
    bedrooms,
    initialMedianPrices
  )

  const [getChartPng, { ref: chartCaptureRef }] = useGenerateImage({
    options: {
      backgroundColor: COLORS.nature[95],
      scale: 2,
    },
  })
  const [chartData, setChartData] = React.useState(
    buildChartData(
      medianPrices?.house?.trend || [],
      medianPrices?.unit?.trend || []
    )
  )

  useEffect(() => {
    setChartData(
      buildChartData(
        medianPrices?.house?.trend || [],
        medianPrices?.unit?.trend || []
      )
    )
  }, [medianPrices])

  useEffect(() => {
    registerPriceTrackerChartCapture(getChartPng)
    return () => unregisterPriceTrackerChartCapture()
  }, [getChartPng])

  if (!medianPrices) return null

  const investerStatisticsData = [
    ...(medianPrices?.house?.price > 0
      ? [
        {
          title: 'Median house price',
          subTitle: '(all agencies, last 12 months)',
          value: (
            <>
              {formatPrice(medianPrices?.house?.price || 0)}
              <sup>*</sup>
            </>
          ),
        },
      ]
      : []),
    ...(medianPrices?.house?.change_percent_year != 0 &&
      typeof medianPrices?.house?.change_percent_year === 'number'
      ? [
        {
          title: 'House price change',
          subTitle: '(all agencies, last 12 months)',
          value: `${medianPrices?.house?.change_percent_year > 0 ? '+' : ''}${medianPrices?.house?.change_percent_year
            }%`,
        },
      ]
      : []),
    ...(medianPrices?.unit?.price > 0
      ? [
        {
          title: 'Median unit price',
          subTitle: '(all agencies, last 12 months)',
          value: (
            <>
              {formatPrice(medianPrices?.unit?.price || 0)}
              <sup>*</sup>
            </>
          ),
        },
      ]
      : []),
    ...(medianPrices?.unit?.change_percent_year != 0 &&
      typeof medianPrices?.unit?.change_percent_year === 'number'
      ? [
        {
          title: 'Unit price change',
          subTitle: '(all agencies, last 12 months)',
          value: `${medianPrices?.unit?.change_percent_year > 0 ? '+' : ''}${medianPrices?.unit?.change_percent_year
            }%`,
        },
      ]
      : []),
  ]

  const ticksAll = buildYearStartTicks(chartData)
  const startMonth = chartData?.length
    ? getMonth(parseISO(chartData[0].date)) // 0-based
    : null

  const yearsInData = getYearsInChartData(chartData)
  const hideFirstYearLabel = startMonth !== null && startMonth >= 3

  // If the first data point starts from April or later, hide the first year label.
  // Keep the underlying tick positions for consistent spacing.
  const ticks =
    hideFirstYearLabel ? ticksAll.slice(1) : ticksAll

  // Avoid adding synthetic Jan padding before the first real data point,
  // so the first dots sit as close as possible to the Y axis.
  const paddingYears = hideFirstYearLabel ? yearsInData.slice(1) : yearsInData
  const axisChartData = extendChartDataForAxis(chartData, ticks, paddingYears)

  const isShowStatistic = investerStatisticsData.length > 0
  const isShowChart = chartData?.length > 0
  const isShowBlock =
    isShowStatistic ||
    initialMedianPrices?.house?.trend?.find((it) => it.price > 0) ||
    initialMedianPrices?.unit?.trend?.find((it) => it.price > 0)

  if (!isShowBlock) return null

  return (
    <Section withPaddingBottom style={{ backgroundColor: COLORS.nature[95] }}>
      <Container>
        <PoweredBy />
        <S.Group>
          <S.GroupHeading>
            <S.Title>MEDIAN PRICES IN {suburb}</S.Title>
            <SuburbProfileFilters
              bedrooms={bedrooms}
              availableBedrooms={availableBedrooms}
              onBedroomsChange={setBedrooms}
              type={type}
              isShowUnit={!!initialMedianPrices?.unit?.trend?.find((it) => it.price > 0)}
              isShowHouse={!!initialMedianPrices?.house?.trend?.find((it) => it.price > 0)}
              onTypeChange={setType}
            />
          </S.GroupHeading>

          <StatisticItems statistics={investerStatisticsData} />
          <S.Note>
            (Median House Price and Median Unit Price is calculated based on
            sales of properties with all numbers of bedrooms)
          </S.Note>
        </S.Group>
        {isShowChart ? (
          <S.Group ref={ref}>
            <S.GroupHeading>
              <S.Title>{suburb} PRICE TRACKER</S.Title>
              <div>
                <S.ChartLegend>
                  {type !== 'unit' &&
                    chartData?.find((it) => it.house)?.house && (
                      <span className='house'>Houses</span>
                    )}
                  {type !== 'house' &&
                    chartData?.find((it) => it.unit)?.unit && (
                      <span className='unit'>Units</span>
                    )}
                </S.ChartLegend>
              </div>
            </S.GroupHeading>

            <div id={PRICE_TRACKER_CHART_ID} ref={chartCaptureRef}>
              <ResponsiveContainer width='100%' height={275}>
                <LineChart data={axisChartData}>
                  <CartesianGrid
                    stroke={COLORS.grey[10]}
                    vertical={false}
                    horizontal={true}
                  />
                  <XAxis
                    dataKey='date'
                    ticks={ticks}
                    interval={0}
                    allowDuplicatedCategory={false}
                    tickFormatter={(d) => format(parseISO(d), 'yyyy')}
                    tickLine={false}
                    axisLine={{ stroke: COLORS.grey[10] }}
                    padding={{ left: 0, right: 24 }}
                    tick={{ fill: COLORS.black, fontSize: 16, dy: 10 }}
                  />
                  <YAxis
                    tickFormatter={formatPrice}
                    tickLine={false}
                    axisLine={{ stroke: COLORS.navi }}
                    tick={{ fill: COLORS.black, fontSize: 14, dx: -10 }}
                  />
                  <Tooltip
                    formatter={(v, name) => {
                      const formattedName =
                        name === 'house'
                          ? 'Houses'
                          : name === 'unit'
                            ? 'Units'
                            : name
                      return [formatPrice(v), formattedName]
                    }}
                    labelFormatter={(d) => format(parseISO(d), 'MMM yyyy')}
                  />
                  {ticks.map((d) => (
                    <ReferenceLine key={d} x={d} stroke={COLORS.navi} />
                  ))}
                  <Line
                    type='monotone'
                    dataKey='house'
                    connectNulls={false}
                    stroke={COLORS.cobalt.base}
                    dot={{
                      r: 4,
                      strokeWidth: 1,
                      fill: COLORS.cobalt.base,
                      stroke: COLORS.white,
                    }}
                    strokeWidth={2}
                    animationDuration={750}
                    animationEasing='ease-in-out'
                    isAnimationActive={inView}
                  />
                  <Line
                    type='monotone'
                    dataKey='unit'
                    connectNulls={false}
                    stroke={COLORS.navi}
                    dot={{
                      r: 4,
                      strokeWidth: 1,
                      fill: COLORS.navi,
                      stroke: COLORS.white,
                    }}
                    strokeWidth={2}
                    animationDuration={750}
                    animationEasing='ease-in-out'
                    isAnimationActive={inView}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </S.Group>
        ) : null}
      </Container>
    </Section>
  )
}

export default MedianPrice
